import React, { useCallback, useEffect } from 'react';
import cn from 'classnames';
import moment from 'moment';

import { fromPathnameToIsBucket, fromPathnameToIsWeekLocked } from 'bl/pathname.bl';
import DatePickerInput from 'components/form/DatePickerInput.component';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromAnyDateToDate, fromAnyDateToIso } from 'utils/converters/time/from.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

const L = logger('DateRangePicker');

const MODE = Object.freeze({
  nil: '',
  bgn: 'start',
  end: 'end',
});

const determineRange = ({ mode, date, isWeekLocked }) => {
  const dateAsMoment = moment(date);

  if (isWeekLocked) return { fromDate: dateAsMoment.clone().weekday('0'), toDate: dateAsMoment.clone().weekday('6') };
  if (mode === MODE.bgn) return { fromDate: dateAsMoment, toDate: null };
  if (mode === MODE.end) return { fromDate: null, toDate: dateAsMoment };

  return void 1;
};

const DateRangePicker = (props) => {
  const { lastDatesSelected, onChangeDate, isFetching, swapToEnd } = props;
  const { t } = useTranslation();

  const { formatDate } = useGilt();

  // FIXME: component should not check where they are put and change behavior because of it, pass a flag through props instead
  const pathname = fromAnyToString(window.location);
  const isInsideBucket = fromPathnameToIsBucket(pathname);
  const isWeekLocked = fromPathnameToIsWeekLocked(pathname);
  const tag = fromAnyToTrimmed(props.tag);

  const [mode, setMode] = useStateWhileMounted(MODE.nil);
  const [dates, setDates] = useStateWhileMounted(lastDatesSelected);

  const { val: isFromDisabled, inv: isFromEnabled, yay: disableFrom, nay: enableFrom } = useTip(false);
  const { val: isToDisabled, inv: isToEnabled, yay: disableTo, nay: enableTo } = useTip(true);

  const bgn = lastDatesSelected ? dates.fromDate || lastDatesSelected.fromDate : void 1;
  const end = lastDatesSelected ? dates.toDate || lastDatesSelected.toDate : void 1;

  const ranges =
    !bgn && !end
      ? void 1
      : [
          {
            startDate: fromAnyDateToDate(mode === MODE.end ? end : bgn),
            endDate: fromAnyDateToDate(mode === MODE.end ? bgn : end),
            key: 'selection',
          },
        ];

  const isoFrom = fromAnyDateToIso(dates?.fromDate);
  const isoTo = fromAnyDateToIso(dates?.toDate);

  const displayFormatDate = useCallback((date) => date && formatDate(date, { hideTime: true, hideWeekday: true }), [formatDate]);

  const handleNextClicked = useCallback(() => void onChangeDate(moment(isoFrom).clone().add(1, 'week'), moment(isoTo).clone().add(1, 'week')), [isoFrom, isoTo, onChangeDate]);

  const handlePrevClicked = useCallback(() => void onChangeDate(moment(isoFrom).clone().add(-1, 'week'), moment(isoTo).clone().add(-1, 'week')), [isoFrom, isoTo, onChangeDate]);

  const handleDateClick = useCallback(
    (ev) => {
      const selection = ev.selection;
      const selectionBgn = selection.startDate;
      const selectionEnd = selection.endDate;

      const interval = determineRange({ mode, date: selectionBgn, isWeekLocked });

      if (!isWeekLocked && selectionBgn === selectionEnd) setDates(interval);
      if (mode === MODE.bgn) enableTo();

      setDates(interval);
      setMode(MODE.nil);

      onChangeDate(interval.fromDate, interval.toDate);
    },
    [enableTo, isWeekLocked, mode, onChangeDate, setDates, setMode],
  );

  const handleClose = useCallback(() => setMode(MODE.nil), [setMode]);

  const handleClickFrom = useCallback(() => {
    L.debug('handleClickFrom()', { mode, isFromEnabled });
    if (isFromEnabled) setMode(MODE.bgn);
  }, [isFromEnabled, mode, setMode]);

  const handleClickTo = useCallback(() => {
    L.debug('handleClickTo()', { mode, bgn, isToEnabled });
    if (bgn && isToEnabled && mode !== MODE.end) setMode(MODE.end);
  }, [bgn, isToEnabled, mode, setMode]);

  useEffect(() => {
    setDates({ ...lastDatesSelected });

    if (isFetching || isInsideBucket) {
      disableTo();
      disableFrom();
    } else {
      enableFrom();
      if (!isWeekLocked) enableTo();
    }
  }, [disableFrom, disableTo, enableFrom, enableTo, isFetching, isInsideBucket, isWeekLocked, lastDatesSelected, setDates]);

  useEffect(() => {
    // NOTE: parent was trying to change this component's state with a ref to this component and the line:
    // this.dateRangeRef.current.setState({ rangePicker: 'end' });
    // that is replaced with the prop swapToEnd now
    if (!swapToEnd) return;
    setMode(MODE.end);
  }, [setMode, swapToEnd]);

  const isoBgn = fromAnyDateToIso(bgn);
  const isoEnd = fromAnyDateToIso(end);

  const isoLastFrom = fromAnyDateToIso(lastDatesSelected?.fromDate);
  const isoLastTo = fromAnyDateToIso(lastDatesSelected?.toDate);

  L.debug('()', { isFromEnabled, isToEnabled, mode }, { bgn, isoBgn, isoFrom, isoLastFrom, end, isoEnd, isoTo, isoLastTo }, { lastDatesSelected, dates, ranges });

  return (
    <div data-bem="DateRangePicker" data-tag={tag} className="rdr-reports">
      <header className={mode ? 'date-input open' : 'date-input'}>
        <span className="next" role="button" tabIndex={0} onClick={handleNextClicked} />
        <span className="prev" role="button" tabIndex={0} onClick={handlePrevClicked} />
        <div className="start-date-label">{t('composite.dateRangePicker.startDate')}</div>
        <div className={mode === MODE.bgn ? 'active' : ' '}>
          <div
            data-bem="DateRangePicker__from"
            className={cn({
              'dpicker-input': true,
              'range-FROM': true,
              disabled: isFromDisabled,
            })}
            style={{ cursor: 'pointer' }}
            role="button"
            onClick={handleClickFrom}
          >
            <p>
              {displayFormatDate(bgn)}
              {!bgn && t('composite.dateRangePicker.fromDate')}
            </p>
          </div>
        </div>
        <div className="end-date-label">{t('composite.dateRangePicker.endDate')}</div>
        <div className={mode === MODE.end ? 'active' : ' '}>
          <div
            data-bem="DateRangePicker__to"
            className={cn({
              'dpicker-input': true,
              'range-TO': true,
              disabled: !bgn || isToDisabled,
            })}
            style={{ cursor: 'pointer' }}
            role="button"
            onClick={handleClickTo}
          >
            <p>
              {displayFormatDate(end)}
              {!end && t('composite.dateRangePicker.toDate')}
            </p>
          </div>
        </div>
      </header>
      {mode && (
        <div>
          <div
            className={`rdr-popup animated zoomIn ${mode}
              ${isWeekLocked ? '' : 'months'}`}
          >
            <div className="m-scroll-y">
              <DatePickerInput
                tag="DateRangePicker__ranges"
                component={DatePickerInput.WeekRangePicker}
                className="PreviewArea"
                months={isWeekLocked ? 1 : 2}
                onChange={handleDateClick}
                ranges={ranges}
                showSelectionPreview
              />
            </div>

            <div role="button" tabIndex={0} onKeyDown={alwaysNoop} style={{ zIndex: 1 }} className={mode ? 'close-overlay' : ''} onClick={handleClose} />
          </div>
        </div>
      )}
    </div>
  );
};

// noinspection JSUnusedGlobalSymbols
export default DateRangePicker;
